
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchOneToManyFunc,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import VisitorsProviderProduct from 'Models/Security/Acl/VisitorsProviderProduct';
import UserProviderProduct from 'Models/Security/Acl/UserProviderProduct';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface IProviderProductEntityAttributes extends IModelAttributes {
	code: string;
	name: string;
	description: string;
	baseCost: number;
	isActive: boolean;
	s3FileKey: string;
	countryCode: string;

	creditReportProducts: Array<
		| Models.BusinessEntityCreditReportSettingsEntity
		| Models.IBusinessEntityCreditReportSettingsEntityAttributes
	>;
	providerProductOrganisations: Array<
		| Models.ProviderProductOrganisationEntity
		| Models.IProviderProductOrganisationEntityAttributes
	>;
	providerId: string;
	provider: Models.ProviderEntity | Models.IProviderEntityAttributes;
	activeOnCreate: boolean;
}

@entity('ProviderProductEntity', 'Provider Product')
export default class ProviderProductEntity extends Model
	implements IProviderProductEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsProviderProduct(),
		new UserProviderProduct(),
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<ProviderProductEntity, string>()
	@CRUD({
		name: 'Code',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public code: string;

	@observable
	@attribute<ProviderProductEntity, string>()
	@CRUD({
		name: 'Name',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public name: string;

	@observable
	@attribute<ProviderProductEntity, string>()
	@CRUD({
		name: 'Description',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public description: string;

	@Validators.Required()
	@Validators.Numeric()
	@observable
	@attribute<ProviderProductEntity, number>()
	@CRUD({
		name: 'Base Cost',
		displayType: 'textfield',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseFloat,
	})
	public baseCost: number;

	@observable
	@attribute<ProviderProductEntity, boolean>()
	@CRUD({
		name: 'Is Active',
		displayType: 'checkbox',
		order: 50,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public isActive: boolean;

	@observable
	@attribute<ProviderProductEntity, string>()
	@CRUD({
		name: 'S3 File Key',
		displayType: 'textfield',
		order: 60,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public s3FileKey: string;

	@observable
	@attribute<ProviderProductEntity, string>()
	@CRUD({
		name: 'Country Code',
		displayType: 'textfield',
		order: 70,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public countryCode: string;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'Credit Report Products',
		displayType: 'reference-multicombobox',
		order: 80,
		referenceTypeFunc: () => Models.BusinessEntityCreditReportSettingsEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'creditReportProducts',
			oppositeEntity: () => Models.BusinessEntityCreditReportSettingsEntity,
		}),
		
	})
	public creditReportProducts: Models.BusinessEntityCreditReportSettingsEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'Provider Product Organisations',
		displayType: 'reference-multicombobox',
		order: 90,
		referenceTypeFunc: () => Models.ProviderProductOrganisationEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'providerProductOrganisations',
			oppositeEntity: () => Models.ProviderProductOrganisationEntity,
		}),
		
	})
	public providerProductOrganisations: Models.ProviderProductOrganisationEntity[] = [];

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'Provider',
		displayType: 'reference-combobox',
		order: 100,
		referenceTypeFunc: () => Models.ProviderEntity,
		
	})
	public providerId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public provider: Models.ProviderEntity;


	@observable
	@attribute<ProviderProductEntity, boolean>()
	@CRUD({
		name: 'Active On Create',
		displayType: 'checkbox',
		order: 110,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public activeOnCreate: boolean;

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IProviderProductEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IProviderProductEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.code !== undefined) {
				this.code = attributes.code;
			}
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.description !== undefined) {
				this.description = attributes.description;
			}
			if (attributes.baseCost !== undefined) {
				this.baseCost = attributes.baseCost;
			}
			if (attributes.isActive !== undefined) {
				this.isActive = attributes.isActive;
			}
			if (attributes.s3FileKey !== undefined) {
				this.s3FileKey = attributes.s3FileKey;
			}
			if (attributes.countryCode !== undefined) {
				this.countryCode = attributes.countryCode;
			}
			if (attributes.creditReportProducts !== undefined && Array.isArray(attributes.creditReportProducts)) {
				for (const model of attributes.creditReportProducts) {
					if (model instanceof Models.BusinessEntityCreditReportSettingsEntity) {
						this.creditReportProducts.push(model);
					} else {
						this.creditReportProducts.push(new Models.BusinessEntityCreditReportSettingsEntity(model));
					}
				}
			}
			if (attributes.providerProductOrganisations !== undefined && Array.isArray(attributes.providerProductOrganisations)) {
				for (const model of attributes.providerProductOrganisations) {
					if (model instanceof Models.ProviderProductOrganisationEntity) {
						this.providerProductOrganisations.push(model);
					} else {
						this.providerProductOrganisations.push(new Models.ProviderProductOrganisationEntity(model));
					}
				}
			}
			if (attributes.providerId !== undefined) {
				this.providerId = attributes.providerId;
			}
			if (attributes.provider !== undefined) {
				if (attributes.provider === null) {
					this.provider = attributes.provider;
				} else if (attributes.provider instanceof Models.ProviderEntity) {
					this.provider = attributes.provider;
					this.providerId = attributes.provider.id;
				} else {
					this.provider = new Models.ProviderEntity(attributes.provider);
					this.providerId = this.provider.id;
				}
			}

			if (attributes.activeOnCreate !== undefined) {
				this.activeOnCreate = attributes.activeOnCreate;
			}
		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		creditReportProducts {
			${Models.BusinessEntityCreditReportSettingsEntity.getAttributes().join('\n')}
		}
		providerProductOrganisations {
			${Models.ProviderProductOrganisationEntity.getAttributes().join('\n')}
		}
		provider {
			${Models.ProviderEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			creditReportProducts: {},
			providerProductOrganisations: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(ProviderProductEntity.prototype, 'created');
CRUD(modifiedAttr)(ProviderProductEntity.prototype, 'modified');
